import { IS_AI_AND_BRAND_KIT_ENABLED } from 'admin/constants/release-features';
import { find, size, trim, noop } from 'lodash';
import React, { ReactNode, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormSubmitHandler, InjectedFormProps, reduxForm, getFormValues, getFormSyncErrors } from 'redux-form';
import type { StoryDefaultPlatform, StoryModel, StoryTemplateWithData, StoryTemplateWithoutData } from 'types/story';
import type { IUserOrganization } from 'types/user';
import t from 'common/utils/translate';
import type { AdminReducerState } from 'admin/reducers';
import { DEFAULT_MEDIA_QUERY_PLATFORMS, STORY_TYPE } from 'common/constants';
import { getBrandKitCreationDefaults, getDefaultBrandKit } from 'common/brand-kit/projection';
import { useClickOutside } from 'common/hooks/use-click-outside';
import { FORM_MODEL, LANG_OPTIONS } from 'admin/constants/common';
import { STORY_NAME_MIN_LEN } from 'admin/components/common/Form/validations';

export interface FormValues {
	name: StoryModel['name'];
	lang: StoryModel['language'];
	type: Exclude<StoryModel['type'], null>;
	defaultPlatform: StoryDefaultPlatform;
	contentType: StoryContentType;
	clone: string;
	contentPrompt: string;
	brandKitId: string;
}

export const fields: { [key in keyof FormValues]: string } = {
	name: 'name',
	lang: 'lang',
	type: 'type',
	defaultPlatform: 'defaultPlatform',
	contentType: 'contentType',
	clone: 'clone',
	contentPrompt: 'contentPrompt',
	brandKitId: 'brandKitId',
};

export type CreateStoryFromTemplateParams = { template: StoryTemplateWithoutData['id'] | StoryTemplateWithData };

export type CreateStoryFromScratchParams = { type: FormValues['type'] };

export enum StoryContentType {
	static = 'static',
	generic = 'generic',
}

const formValues = getFormValues(FORM_MODEL.CREATE_STORY);

const syncErrors = getFormSyncErrors(FORM_MODEL.CREATE_STORY);

type OwnProps = {
	organization: IUserOrganization;
	className?: string;
	onSubmit?: FormSubmitHandler<FormValues, OwnProps>;
	onClickOutside?: () => void;
	children?: (
		props: Omit<Props, 'organization' | 'className' | 'children' | 'onSubmit' | 'onFormClick'>
	) => ReactNode;
};

const mapStateToProps = (state: AdminReducerState, { organization }: OwnProps) => {
	const defaultStoryLang = organization?.metadata?.storyLang;
	const defaultBrandKit = IS_AI_AND_BRAND_KIT_ENABLED
		? getDefaultBrandKit(organization?.metadata?.brandKits)
		: undefined;
	const organizationLanguage = (find(LANG_OPTIONS, { value: defaultStoryLang })?.value ??
		LANG_OPTIONS[0].value) as FormValues['lang'];
	const { language } = getBrandKitCreationDefaults(organization?.metadata?.brandKits, defaultBrandKit?.id ?? null, {
		language: organizationLanguage,
	});

	return {
		initialValues: {
			[fields.name]: '',
			[fields.type]: STORY_TYPE.STANDALONE,
			[fields.lang]: language,
			[fields.defaultPlatform]: DEFAULT_MEDIA_QUERY_PLATFORMS.DESKTOP,
			[fields.contentType]: StoryContentType.static,
			[fields.clone]: '',
			[fields.contentPrompt]: '',
			[fields.brandKitId]: defaultBrandKit?.id ?? '',
		},
		formValues: formValues(state) as FormValues,
		formErrors: syncErrors(state) as FormValues,
	};
};

const connector = connect(mapStateToProps);

export type ReduxFormProps = InjectedFormProps<FormValues, PureProps>;

type PureProps = ConnectedProps<typeof connector> & OwnProps;

type Props = PureProps & ReduxFormProps;

const Form = ({ organization: _organization, className = '', children, onSubmit, onClickOutside, ...props }: Props) => {
	const formRef = useRef<HTMLFormElement | null>(null);

	useClickOutside(formRef, onClickOutside || noop);

	return (
		<form className={className} onSubmit={props.handleSubmit(onSubmit || noop)} ref={formRef}>
			{children?.(props)}
		</form>
	);
};

const CreateStoryModalForm = reduxForm<FormValues, PureProps>({
	form: FORM_MODEL.CREATE_STORY, //  <------ same form name
	destroyOnUnmount: false, //        <------ preserve form data
	forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
	validate: values => {
		const errors: Partial<FormValues> = {};

		if (values.contentType === StoryContentType.static) {
			if (!values.name) {
				errors[fields.name] = t(`createStoryModal.fields.${fields.name}.required`);
			} else if (size(trim(values.name)) < STORY_NAME_MIN_LEN) {
				errors[fields.name] = t(`createStoryModal.fields.${fields.name}.length`);
			}
		}

		if (values.contentType === StoryContentType.generic) {
			if (!values.contentPrompt) {
				errors[fields.contentPrompt] = t(`createStoryModal.fields.${fields.contentPrompt}.required`);
			}
		}

		if (!values.lang) {
			errors[fields.lang] = t(`createStoryModal.fields.${fields.lang}.required`);
		}

		return errors;
	},
})(Form);

export default connector(CreateStoryModalForm);
